import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '@/app/components/ui/use-toast';
import { isEnterpriseDistribution } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { ElementType, SortByType } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { type ElementsSelectorProps, HEAD_TAB } from '../types';
import { usePersonalElements } from './use-personal-elements';
import { useShareElements } from './use-share-elements';

export function useElementSelector(props: ElementsSelectorProps) {
  const {
    isOpen,
    onClose,
    onSelect,
    remainingSelections = 6,
    maxReferenceCount,
    onDelete,
    disabledIds,
    onItemChange,
    allowedModalities,
  } = props;

  const t = useTranslations();

  const [selectedElements, setSelectedElements] = useState<ElementType[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [elements, setElements] = useState<ElementType[]>([]);
  const { referenceModalUpdateReason, userInfo } = useStore((state) => ({
    userInfo: state.userInfo,
    referenceModalUpdateReason: state.referenceModalUpdateReason,
  }));

  const [activeHeadTab, setActiveHeadTab] = useState<HEAD_TAB>(
    userInfo ? HEAD_TAB.REFERENCE : HEAD_TAB.SHARE_REFERENCE,
  );

  const headTabs = useMemo(() => {
    return [
      {
        text: t('elements.myElement'),
        key: HEAD_TAB.REFERENCE,
        // 未登录时只显示社区主体库 tab
        disabled: !userInfo,
      },
      {
        text: t('elements.shareReference'),
        key: HEAD_TAB.SHARE_REFERENCE,
        disabled: isEnterpriseDistribution,
      },
    ].filter((tab) => !tab.disabled);
  }, [userInfo]);

  const sortOptions: {
    label: string;
    value: SortByType;
  }[] = useMemo(
    () => [
      {
        label: t('shareReference.rank.recommend'),
        value: 'recommend',
      },
      {
        label: t('shareReference.rank.new'),
        value: 'time',
      },
    ],
    [],
  );

  const {
    collections,
    activeCategory,
    activeCollection,
    setActiveCategory,
    setActiveCollection,
    sortBy,
    setSortBy,
    categories,
    getShareReferenceData,
    onExpose,
    postReferenceListReq,
  } = useShareElements({
    refreshToken,
    activeHeadTab,
    setElements,
    searchValue,
    elements,
    allowedModalities,
  });

  const updateRefreshToken = useCallback(() => {
    setRefreshToken(uuidv4());
  }, []);

  const { tags, selectedTags, deleteTag, handleTagSelectorChange, elementsListReq, getTags, selectAll } =
    usePersonalElements({
      refreshToken,
      activeHeadTab,
      setElements,
      searchValue,
      elements,
      allowedModalities,
      updateRefreshToken,
      onItemChange,
      onDelete,
      setSelectedElements,
    });

  /**
   * 切换元素选中状态
   */
  const toggleElementSelection = useCallback((element: ElementType) => {
    setSelectedElements((prev) => {
      const isSelected = prev.some((item) => item.id === element.id);
      if (isSelected) {
        return prev.filter((item) => item.id !== element.id);
      }
      return [...prev, element];
    });
  }, []);

  /**
   * 完成选择
   */
  const handleDone = useCallback(() => {
    if (remainingSelections && selectedElements.length > remainingSelections) {
      toast({
        type: 'warning',
        message: allowedModalities?.includes('video')
          ? t('elements.elementDialog.elementOverLimitStrVideo', {
              count: maxReferenceCount || remainingSelections,
            })
          : t('elements.elementDialog.elementOverLimitStrImage', {
              count: maxReferenceCount || remainingSelections,
            }),
      });
      return;
    }
    reportEvent('element_selector_page_click', {
      click_position: 'done',
    });
    onSelect(selectedElements);
    onClose();
  }, [remainingSelections, selectedElements, allowedModalities, maxReferenceCount, onSelect, onClose]);

  /**
   * 处理搜索值变化
   */
  const handleSetSearchValue = useCallback((value: string) => {
    setSearchValue(value);
    value &&
      reportEvent('search_click', {
        position: 'create',
      });
  }, []);

  /**
   * 切换头部 Tab
   */
  const onHeadTabChange = useCallback((value: string) => {
    setActiveHeadTab(value as HEAD_TAB);
    setSearchValue('');
    updateRefreshToken();
    setElements([]);
  }, []);

  useEffect(() => {
    setSelectedElements([]);
  }, [allowedModalities]);

  useEffect(() => {
    if (referenceModalUpdateReason?.type === 'forbidden') {
      updateRefreshToken();
    }
  }, [referenceModalUpdateReason]);

  /**
   * 监听 isOpen 变化
   */
  useEffect(() => {
    if (isOpen) {
      reportEvent('element_selector_page_view');
      // 只有登录用户才请求标签接口
      if (userInfo) {
        getTags();
      }
      // 未登录时默认显示社区主体库
      setActiveHeadTab(userInfo ? HEAD_TAB.REFERENCE : HEAD_TAB.SHARE_REFERENCE);
    } else {
      setSelectedElements([]);
      selectAll();
    }
  }, [isOpen, userInfo]);

  // ==================== 返回值 ====================
  return {
    selectedElements,
    searchValue,
    setSearchValue,
    activeHeadTab,
    setActiveHeadTab,
    elements,
    collections,
    activeCategory,
    activeCollection,
    setActiveCategory,
    setActiveCollection,
    sortBy,
    setSortBy,
    tags,
    selectedTags,
    categories,

    toggleElementSelection,
    setSelectedElements,
    handleDone,
    deleteTag,
    handleSetSearchValue,
    handleTagSelectorChange,
    onHeadTabChange,
    getShareReferenceData,
    onExpose,

    elementsListReq,
    postReferenceListReq,

    headTabs,
    sortOptions,
    disabledIds,
    allowedModalities,
  };
}
